import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import { tokens } from '@hotjar/hotsauce';
import SelectArrow from '@hotjar/shared-assets/images/select-arrow.png';

const SelectCommon = styled.select`
  display: block;
  padding: 6px 12px;
  font-size: 14px;
  border: 1px solid ${tokens.color.ui_border};
  color: ${tokens.color.text_default};
  width: 100%;
  height: 40px;
  padding-top: 0;
  padding-bottom: 0;
  border-radius: 2px;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.12);
  background: ${tokens.color.ui_background} none no-repeat;
  line-height: 1.4em;
  padding-right: 34px;
  position: relative;
  background-size: 34px;
  background-image: url(${SelectArrow});
  -webkit-appearance: none;
  background-position: right center;

  /* for IE10+ */
  &::-ms-expand {
    display: none;
  }

  &:hover {
    border-color: ${tokens.color.ui_border_alt};
    ${tokens.elevation(1)};
  }

  &:focus {
    box-shadow: 0 0 0 1px ${tokens.color.ui_background}, 0 0 0 3px ${tokens.color.focus};
  }

  &[disabled] {
    background-color: ${tokens.color.ui_disabled};
    border: 1px solid ${tokens.color.ui_disabled};
    color: ${tokens.color.text_default};
    box-shadow: none;
  }
`;

const sStyles = css`
  font-size: 12px;
  color: ${tokens.color.text_default};
  width: auto;
  height: 28px;
  padding: 5px 5px;
  border-radius: 3px;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.06);
  padding-right: 22px;
  background-size: 22px;
`;

const lStyles = css`
  padding: 10px 16px;
  font-size: 16px;
  height: 50px;
`;

const errorStyles = css`
  border-color: ${tokens.color.ui_border_danger};

  &[disabled] {
    border-color: ${tokens.color.ui_border_danger};
  }
`;

export const sizeStyles = {
  s: sStyles,
  l: lStyles,
} as const;

const errorMessageStyles = css`
  color: ${tokens.color.text_danger};
  margin-top: 4px;
  font-size: 12px;
`;

interface SelectProps extends Omit<React.HTMLProps<HTMLSelectElement>, 'size' | 'as'> {
  isError?: boolean;
  size?: keyof typeof sizeStyles;
  errorMessage?: string;
  containerCss?: SerializedStyles;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ isError, disabled, children, size, errorMessage, containerCss, ...otherProps }, ref) => {
    const styles = [];

    if (isError) {
      styles.push(errorStyles);
    }

    if (size && sizeStyles[size]) {
      // Add button sizing styles if we have a size prop.
      styles.push(sizeStyles[size]);
    }

    return (
      <div css={containerCss}>
        <SelectCommon css={styles} disabled={disabled} ref={ref} {...otherProps}>
          {children}
        </SelectCommon>
        {isError && errorMessage && <div css={errorMessageStyles}>{errorMessage}</div>}
      </div>
    );
  },
);

Select.displayName = 'Select';
